// src/views/NewsView.js
import React, { useState, useEffect, useMemo } from 'react';
import { Search, Loader2, Newspaper, Globe, ExternalLink } from 'lucide-react';

const MASSIVE_KEY = "QjR8RLPBjbEmmwd4D3mZiDUAVYEq5kEK";

export default function NewsView({ themeClasses }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTicker, setSearchTicker] = useState("");
  const [debouncedTicker, setDebouncedTicker] = useState("");
  const [nextUrl, setNextUrl] = useState(null);
  const [publisherFilter, setPublisherFilter] = useState("ALL");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTicker(searchTicker), 500);
    return () => clearTimeout(timer);
  }, [searchTicker]);

  useEffect(() => {
    setLoading(true);
    setArticles([]);
    setNextUrl(null);
    const query = debouncedTicker ? `&ticker=${debouncedTicker.toUpperCase()}` : "";
    fetch(`https://api.massive.com/v2/reference/news?limit=30${query}&apiKey=${MASSIVE_KEY}`)
      .then(r => r.json())
      .then(data => {
        setArticles(data.results || []);
        setNextUrl(data.next_url || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [debouncedTicker]);

  const fetchMore = () => {
    if (!nextUrl) return;
    setLoadingMore(true);
    fetch(`${nextUrl}&apiKey=${MASSIVE_KEY}`)
      .then(r => r.json())
      .then(data => {
        setArticles(prev => [...prev, ...(data.results || [])]);
        setNextUrl(data.next_url || null);
        setLoadingMore(false);
      })
      .catch(() => setLoadingMore(false));
  };

  const uniquePublishers = useMemo(() => {
    const pubs = new Set(articles.map(a => a.publisher?.name).filter(Boolean));
    return Array.from(pubs).sort();
  }, [articles]);

  const displayedArticles = publisherFilter === "ALL" 
    ? articles 
    : articles.filter(a => a.publisher?.name === publisherFilter);

  const topSites = [
    { name: "Bloomberg", url: "https://www.bloomberg.com" },
    { name: "Wall Street Journal", url: "https://www.wsj.com" },
    { name: "Financial Times", url: "https://www.ft.com" },
    { name: "Reuters", url: "https://www.reuters.com/finance" },
    { name: "CNBC", url: "https://www.cnbc.com" },
    { name: "Investopedia", url: "https://www.investopedia.com" },
    { name: "Seeking Alpha", url: "https://seekingalpha.com" },
    { name: "MarketWatch", url: "https://www.marketwatch.com" },
    { name: "Yahoo Finance", url: "https://finance.yahoo.com" },
    { name: "Barron's", url: "https://www.barrons.com" }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-12 h-full flex flex-col">
       <div className="shrink-0 mb-4">
          <h1 className={`text-2xl sm:text-3xl font-black ${themeClasses.textHead} flex items-center gap-3`}>
          News
          </h1>
        </div>

       <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 flex-1 min-h-0">
         <div className={`col-span-2 ${themeClasses.bgCard} rounded-3xl border ${themeClasses.border} shadow-xl flex flex-col min-h-0 overflow-hidden`}>
            <div className={`p-6 border-b ${themeClasses.border} ${themeClasses.input} flex gap-4 items-center shrink-0`}>
               <div className="relative flex-1 max-w-md">
                 <Search size={16} className={`absolute left-4 top-3.5 ${themeClasses.textMuted}`} />
                 <input value={searchTicker} onChange={e=>setSearchTicker(e.target.value)} placeholder="Filter news by ticker (e.g. TSLA)..." className={`w-full ${themeClasses.bgCard} border ${themeClasses.border} text-sm pl-12 pr-4 py-3 rounded-xl outline-none focus:border-indigo-500 ${themeClasses.textHead} font-bold`} />
               </div>
               <div>
                 <select value={publisherFilter} onChange={e=>setPublisherFilter(e.target.value)} className={`${themeClasses.bgCard} border ${themeClasses.border} text-sm px-4 py-3 rounded-xl outline-none focus:border-indigo-500 ${themeClasses.textHead} font-bold uppercase tracking-widest`}>
                   <option value="ALL">All Publishers</option>
                   {uniquePublishers.map(p => <option key={p} value={p}>{p}</option>)}
                 </select>
               </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-0">
               {loading ? (
                  <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-indigo-500" size={32}/></div>
               ) : displayedArticles.length === 0 ? (
                  <div className={`flex items-center justify-center h-full ${themeClasses.textMuted} font-black uppercase tracking-widest text-xs`}>No recent news found.</div>
               ) : (
                 <div className="flex flex-col">
                   {displayedArticles.map((article, i) => (
                     <a key={i} href={article.article_url} target="_blank" rel="noreferrer" className={`flex gap-6 p-6 border-b ${themeClasses.border} hover:bg-indigo-500/5 transition-colors group`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest bg-indigo-500/10 px-2 py-1 rounded">{article.publisher?.name || 'News'}</span>
                            <span className={`text-[10px] ${themeClasses.textMuted} font-mono`}>{new Date(article.published_utc).toLocaleString()}</span>
                          </div>
                          <h3 className={`text-base font-black ${themeClasses.textHead} leading-snug mb-2 group-hover:text-indigo-400 transition-colors`}>{article.title}</h3>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {article.tickers?.slice(0, 8).map(t => <span key={t} className={`text-[9px] font-bold ${themeClasses.input} border ${themeClasses.border} px-2 py-0.5 rounded ${themeClasses.textMuted}`}>{t}</span>)}
                          </div>
                        </div>
                     </a>
                   ))}
                   {nextUrl && publisherFilter === "ALL" && (
                      <div className="p-6 text-center">
                        <button onClick={fetchMore} disabled={loadingMore} className="bg-indigo-600/10 border border-indigo-500/30 text-indigo-500 px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-colors">
                          {loadingMore ? <Loader2 size={14} className="animate-spin inline"/> : 'Load More News'}
                        </button>
                      </div>
                   )}
                 </div>
               )}
            </div>
         </div>

         <div className={`col-span-1 ${themeClasses.bgCard} rounded-3xl border ${themeClasses.border} shadow-xl flex flex-col min-h-0 overflow-hidden`}>
            <div className={`p-6 border-b ${themeClasses.border} ${themeClasses.input} shrink-0`}>
              <h3 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest"><Globe size={14} className="inline mr-2"/> Top 10 Investment Resources</h3>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">
              {topSites.map((site, idx) => (
                 <a key={idx} href={site.url} target="_blank" rel="noreferrer" className={`block p-4 rounded-xl border ${themeClasses.border} hover:border-indigo-500 transition-colors group`}>
                   <h4 className={`text-sm font-black ${themeClasses.textHead} group-hover:text-indigo-400 transition-colors flex items-center justify-between`}>
                     <span>{idx + 1}. {site.name}</span>
                     <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 transition-opacity"/>
                   </h4>
                 </a>
              ))}
            </div>
         </div>
       </div>
    </div>
  );
}